import React, { createContext, useState, useEffect } from "react";

export const ThemeContext = createContext({
  isDark: true,
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  // Check if we're in the browser environment
  const isBrowser = typeof window !== "undefined";

  // Initialize theme from localStorage or default to dark mode
  const [isDark, setIsDark] = useState(() => {
    if (isBrowser) {
      const savedTheme = localStorage.getItem("theme");
      // If theme is saved in localStorage, use that value, otherwise default to dark
      return savedTheme ? savedTheme === "dark" : true;
    }
    return true;
  });

  // Add a style tag to the head to prevent flash of white
  useEffect(() => {
    if (isBrowser) {
      // Remove any existing theme style tag
      const existingStyle = document.getElementById("theme-color-scheme");
      if (existingStyle) {
        existingStyle.remove();
      }

      // Create and add the style tag
      const style = document.createElement("style");
      style.id = "theme-color-scheme";
      style.innerHTML = `
        :root {
          color-scheme: ${isDark ? "dark" : "light"};
        }
      `;
      document.head.appendChild(style);
    }
  }, [isDark, isBrowser]);

  // Update localStorage and apply body class when theme changes
  useEffect(() => {
    if (isBrowser) {
      localStorage.setItem("theme", isDark ? "dark" : "light");

      // Apply or remove dark class on body
      if (isDark) {
        document.body.classList.add("dark-mode");
        document.body.classList.remove("light-mode");
      } else {
        document.body.classList.remove("dark-mode");
        document.body.classList.add("light-mode");
      }
    }
  }, [isDark, isBrowser]);

  // Apply theme immediately on mount
  useEffect(() => {
    if (isBrowser) {
      // This ensures the theme is applied as soon as the component mounts
      if (isDark) {
        document.body.classList.add("dark-mode");
        document.body.classList.remove("light-mode");
      } else {
        document.body.classList.remove("dark-mode");
        document.body.classList.add("light-mode");
      }
    }
  }, [isBrowser]);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setIsDark((prevMode) => !prevMode);
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
